/**
 * ScatterPlot — pack widget showing X-Y scatter of N samples, coloured
 * optionally by a categorical (e.g. pass/fail).
 *
 * Source: ported from
 *   D:\WORK\SAHAS\Projects\Digital Twin Concept\app\src\components\correlation\CorrelationView.tsx
 * Adapted to DivineIoT's typed widget contract.
 */

import { useMemo } from "react";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { cn } from "@/lib/ui/cn";
import { yAxisDomain } from "@/lib/widgets";
import { SERIES_PALETTE } from "@/lib/widgets/series-palette";

export interface ScatterPoint {
  x: number;
  y: number;
  /** Optional category — drives marker colour. */
  category?: "pass" | "fail" | "neutral";
  /** Optional id passed back on hover/click. */
  id?: string;
  /** Optional label for tooltip. */
  label?: string;
}

export interface ScatterPlotProps {
  xLabel: string;
  yLabel: string;
  xUnit?: string;
  yUnit?: string;
  points: ScatterPoint[];
  /** Optional reference line for the best-fit linear regression. */
  showTrendline?: boolean;
  /**
   * Fixed lower bound for the Y axis. Omit for autorange (the default).
   * The X axis always auto-fits. When both Y bounds are set the axis
   * clips to the window rather than expanding for outliers.
   */
  yMin?: number;
  /** Fixed upper bound for the Y axis. Omit for autorange. */
  yMax?: number;
  height?: number;
  className?: string;
}

const CATEGORY_COLOR = {
  pass: "rgb(var(--color-success))",
  fail: "rgb(var(--color-danger))",
  neutral: SERIES_PALETTE[0],
} as const;

export function ScatterPlot({
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  points,
  showTrendline = false,
  yMin,
  yMax,
  height = 320,
  className,
}: ScatterPlotProps) {
  const yAxis = yAxisDomain(yMin, yMax);
  const trendline = useMemo(() => {
    if (!showTrendline || points.length < 3) return null;
    const n = points.length;
    const sx = points.reduce((s, p) => s + p.x, 0);
    const sy = points.reduce((s, p) => s + p.y, 0);
    const sxx = points.reduce((s, p) => s + p.x * p.x, 0);
    const sxy = points.reduce((s, p) => s + p.x * p.y, 0);
    const denom = n * sxx - sx * sx;
    if (denom === 0) return null;
    const slope = (n * sxy - sx * sy) / denom;
    const intercept = (sy - slope * sx) / n;
    const xMin = Math.min(...points.map((p) => p.x));
    const xMax = Math.max(...points.map((p) => p.x));
    return [
      { x: xMin, y: slope * xMin + intercept },
      { x: xMax, y: slope * xMax + intercept },
    ];
  }, [points, showTrendline]);

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 12, right: 24, bottom: 36, left: 24 }}>
          <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="x"
            name={xLabel}
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            label={{
              value: xUnit ? `${xLabel} (${xUnit})` : xLabel,
              position: "insideBottom",
              offset: -12,
              style: { fontSize: 11, fill: "rgb(var(--color-on-surface-muted))" },
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name={yLabel}
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            domain={yAxis.domain}
            allowDataOverflow={yAxis.allowDataOverflow}
            label={{
              value: yUnit ? `${yLabel} (${yUnit})` : yLabel,
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11, fill: "rgb(var(--color-on-surface-muted))", textAnchor: "middle" },
            }}
          />
          <ZAxis range={[40, 40]} />
          <Tooltip content={<ScatterTooltip xLabel={xLabel} yLabel={yLabel} xUnit={xUnit} yUnit={yUnit} />} cursor={{ strokeDasharray: "3 3" }} />
          <Scatter data={points} fill={SERIES_PALETTE[0]} isAnimationActive={false}>
            {points.map((p, i) => (
              <Cell key={i} fill={CATEGORY_COLOR[p.category ?? "neutral"]} />
            ))}
          </Scatter>
          {trendline && (
            <Scatter
              data={trendline}
              line={{ stroke: "rgb(var(--color-on-surface-muted))", strokeWidth: 1.5, strokeDasharray: "4 2" }}
              fill="transparent"
              isAnimationActive={false}
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

function ScatterTooltip({
  active,
  payload,
  xLabel,
  yLabel,
  xUnit,
  yUnit,
}: {
  active?: boolean;
  payload?: Array<{ payload: ScatterPoint }>;
  xLabel: string;
  yLabel: string;
  xUnit?: string;
  yUnit?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-surface-elevated p-2 text-xs shadow-md">
      {p.label && <div className="mb-1 font-medium">{p.label}</div>}
      <div className="flex gap-3">
        <div>
          <span className="text-on-surface-muted">{xLabel}: </span>
          <span className="font-mono">
            {p.x.toFixed(2)}
            {xUnit ? ` ${xUnit}` : ""}
          </span>
        </div>
        <div>
          <span className="text-on-surface-muted">{yLabel}: </span>
          <span className="font-mono">
            {p.y.toFixed(2)}
            {yUnit ? ` ${yUnit}` : ""}
          </span>
        </div>
      </div>
      {p.category && (
        <div className="mt-1">
          <span className="text-on-surface-muted">Outcome: </span>
          <span
            className="font-semibold"
            style={{ color: CATEGORY_COLOR[p.category] }}
          >
            {p.category.toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
}
